import React, { useCallback, useMemo } from 'react'
import { useHistory } from 'react-router'
import { t } from 'src/translations'

import { useDeleteAccountMutation } from 'src/apollo/onlineOrdering'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import {
  isToastLocalRequest,
  TOAST_LOCAL_ACCOUNT_PATH,
  TOAST_LOCAL_PATH
} from 'public/js/siteUtilities'
import Button from 'shared/components/common/button'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useModal
} from 'shared/components/common/modal'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'

import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { alertError } from 'shared/js/alertUtils'
import { ChevronRightIcon } from '@toasttab/buffet-pui-icons'
import { getPath } from 'src/lib/js/utilities'
import { CommunicationPreferencesModal } from './CommunicationPreferencesModal'
import { useMarketing } from 'public/components/online_ordering/MarketingContext'

const toastFail = () => {
  alertError(
    'There was an error deleting the account. Please refresh the page.'
  )
}

export const AccountSettings = () => {
  const { customer } = useCustomer()
  const { smsAccountEnabled } = useMarketing()
  const {
    isOpen: isCommunicationsModalOpen,
    onOpen: onOpenCommunicationsModal,
    onClose: onCloseCommunicationsModal
  } = useModal()
  const {
    isOpen: isDeleteAccountModalOpen,
    onOpen: onOpenDeleteAccountModal,
    onClose: onCloseDeleteAccountModal
  } = useModal()
  const isToastLocalAccountPage = useMemo(
    () => getPath() === TOAST_LOCAL_ACCOUNT_PATH || isToastLocalRequest(),
    []
  )
  const showCommunicationPreferences = useMemo(
    () =>
      !isToastLocalAccountPage &&
      ((smsAccountEnabled && customer?.phone) || customer?.email),
    [
      isToastLocalAccountPage,
      smsAccountEnabled,
      customer?.phone,
      customer?.email
    ]
  )

  return (
    <div
      className='accountSection accountSettings'
      data-testid='AccountSettings'
      tabIndex={0}
    >
      <div className='sectionHeader'>
        <h2 className='sectionTitle'>{t('account-settings.account')}</h2>
      </div>
      <div className='actions'>
        {showCommunicationPreferences && (
          <Button onClick={onOpenCommunicationsModal} disabled={!customer}>
            {t('account-settings.communication')}
            <ChevronRightIcon accessibility='decorative' />
          </Button>
        )}
        <Button
          className='deleteAccountButton'
          onClick={onOpenDeleteAccountModal}
          disabled={!customer}
        >
          {t('account-settings.delete-this-account')}
          <ChevronRightIcon accessibility='decorative' />
        </Button>
      </div>
      {isCommunicationsModalOpen && (
        <CommunicationPreferencesModal
          isOpen={isCommunicationsModalOpen}
          onClose={onCloseCommunicationsModal}
        />
      )}
      <DeleteAccountModal
        isOpen={isDeleteAccountModalOpen}
        onClose={onCloseDeleteAccountModal}
      />
    </div>
  )
}

const DeleteAccountModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const { pwlessLogout } = useCustomer()
  const [deleteAccount] = useDeleteAccountMutation()
  const history = useHistory()
  const { orderPath, homePath } = useRestaurantRoutes()
  const ooClient = useOOClient()

  const accountDelete = useCallback(async () => {
    try {
      const { data } = await deleteAccount({ client: ooClient })
      if (data?.deleteAccount.__typename === 'DeleteAccountResponse') {
        const success = await pwlessLogout()
        if (success) {
          if (homePath === TOAST_LOCAL_PATH) {
            history.push(homePath)
          } else {
            orderPath && history.push(orderPath)
          }
        } else {
          toastFail()
        }
      }
    } catch (err) {
      toastFail()
    } finally {
      onClose()
    }
  }, [
    deleteAccount,
    history,
    onClose,
    orderPath,
    homePath,
    pwlessLogout,
    ooClient
  ])

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay fadeIn={false} />
      <ModalContent contentClassName='modalContentV2 bottomDrawerMobile accountPageModal'>
        <div className='modalHeader'>
          <div>{t('account-settings.delete-my-account')}</div>
          <ModalCloseButton />
        </div>
        <div className='modalBody'>
          <div className='accountSettingsModalContent'>
            {t('account-settings.deletion-warning')}
          </div>
          <div className='footer'>
            <Button
              onClick={onClose}
              className='cancelButton'
              data-testid='delete-account-cancel'
            >
              {t('account-settings.cancel')}
            </Button>
            <Button className='deleteAccountButton' onClick={accountDelete}>
              {t('account-settings.delete-my-account')}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
